import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { environment } from '@env';
import { AuthActions } from '@core/store/auth/auth.actions';
import { Store } from '@ngxs/store';
import { ToastService } from '@shared/services/toast.service';
import { Observable, catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';

/** `ProfileApi.getUserInfo()` / `update()` — the read every signed-in load starts with. */
const PROFILE_URL = `${environment.apiUrl}/profile`;

/** Same copy the CV upload shows when the API answers 401 (`cv-upload-copy.ts`). */
const SESSION_REJECTED_MESSAGE = 'Tu sesión venció. Vuelve a iniciar sesión.';

/**
 * The public CV intake is for candidates without an account: a recruiter who happens to be signed
 * in on the same browser must not have their Firebase token sent along with a candidate's CV.
 */
const PUBLIC_CV_INTAKE_PATH = '/public-cv-intake/';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  /**
   * Resolved lazily, only when a session is rejected: NGXS states inject the generated API
   * services, which depend on `HttpClient` and therefore on this interceptor.
   */
  private readonly injector = inject(Injector);
  /** Concurrent profile reads (sign-in + guards) all 401 together: end the session once. */
  private endingRejectedSession = false;

  constructor(private afAuth: AngularFireAuth) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Identify the app of origin so the API can pick the right welcome email
    // (welcome-reclutador vs welcome-tribe) on first registration/login.
    const appReq = req.clone({ setHeaders: { 'X-App-Origin': 'recruiter' } });
    return from(this.afAuth.currentUser).pipe(
      switchMap(async (user) => {
        if (!user || appReq.url.includes(PUBLIC_CV_INTAKE_PATH)) return appReq;
        const token = await user.getIdToken();
        return appReq.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      }),
      switchMap((authReq) =>
        next.handle(authReq).pipe(
          catchError((err: unknown) => {
            if (this.isRejectedSession(authReq, err)) {
              void this.endRejectedSession();
            }
            return throwError(() => err);
          }),
        ),
      ),
    );
  }

  /**
   * Issue #1199 — Firebase accepted the login, but the API answers 401 to the profile read (e.g. a
   * Firebase identity with no `User` row). Only with a token attached: without one, a 401 just
   * means nobody is signed in (the confirm-email page refreshes the profile while signed out).
   */
  private isRejectedSession(req: HttpRequest<unknown>, err: unknown): boolean {
    return (
      err instanceof HttpErrorResponse &&
      err.status === 401 &&
      req.url === PROFILE_URL &&
      req.headers.has('Authorization')
    );
  }

  /**
   * Ends the half-authenticated session: the regular sign-out (Firebase plus every account store),
   * a message, and the login page. The error still reaches its caller first, so the profile state
   * settles before the sign-out resets it. No retry: back on login, nothing reads the profile
   * until the recruiter signs in again.
   */
  private async endRejectedSession(): Promise<void> {
    if (this.endingRejectedSession) {
      return;
    }
    this.endingRejectedSession = true;
    try {
      if (!(await this.afAuth.currentUser)) {
        return;
      }
      await firstValueFrom(this.injector.get(Store).dispatch(new AuthActions.SignOut()));
      this.injector.get(ToastService).show({ type: 'error', title: SESSION_REJECTED_MESSAGE });
      void this.injector.get(Router).navigate(['/reclutador/login']);
    } catch {
      // Sign-out failed: the session is still live, so stay put rather than loop through login.
    } finally {
      this.endingRejectedSession = false;
    }
  }
}
